import { create } from 'zustand';
import { 
  collection, 
  doc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';
import { db } from '../services/firebase';

export const useSolutionStore = create((set, get) => ({
  // State
  solutions: [],
  categories: [],
  isLoading: false,
  hasMore: true,
  lastDoc: null,
  error: null,
  
  // Filters
  filters: {
    subject: '',
    gradeLevel: '',
    topic: '',
    subtopic: '',
    difficulty: '',
    type: '',
    searchTerm: ''
  },

  // Actions
  setFilters: (newFilters) => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
      solutions: [], // Reset solutions when filters change
      lastDoc: null,
      hasMore: true
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        subject: '',
        gradeLevel: '',
        topic: '',
        subtopic: '',
        difficulty: '',
        type: '',
        searchTerm: ''
      },
      solutions: [],
      lastDoc: null,
      hasMore: true
    });
  },

  // Load solutions with pagination
  loadSolutions: async (loadMore = false) => {
    const { filters, lastDoc, solutions } = get();
    
    try {
      set({ isLoading: true, error: null });
      
      let q = collection(db, 'sigma-solutions');
      
      // Apply filters
      const constraints = [];
      
      if (filters.subject) {
        constraints.push(where('subject', '==', filters.subject));
      }
      
      if (filters.gradeLevel) {
        constraints.push(where('gradeLevel', '==', filters.gradeLevel));
      }
      
      if (filters.topic) {
        constraints.push(where('topic', '==', filters.topic));
      }
      
      if (filters.difficulty) {
        constraints.push(where('difficulty', '==', filters.difficulty));
      }
      
      if (filters.type) {
        constraints.push(where('type', '==', filters.type));
      }
      
      // Add ordering and pagination
      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(20));
      
      if (loadMore && lastDoc) {
        constraints.push(startAfter(lastDoc));
      }
      
      q = query(q, ...constraints);
      
      const querySnapshot = await getDocs(q);
      
      const newSolutions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      }));
      
      // Filter by search term on client side (for flexibility)
      const filteredSolutions = filters.searchTerm 
        ? newSolutions.filter(solution => 
            solution.title?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            solution.description?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            solution.content?.toLowerCase().includes(filters.searchTerm.toLowerCase())
          )
        : newSolutions;
      
      const lastDocument = querySnapshot.docs[querySnapshot.docs.length - 1];
      
      set({
        solutions: loadMore ? [...solutions, ...filteredSolutions] : filteredSolutions,
        lastDoc: lastDocument,
        hasMore: querySnapshot.docs.length === 20,
        isLoading: false
      });
      
    } catch (error) {
      console.error('Error loading solutions:', error);
      set({ error: error.message, isLoading: false });
    }
  },

  // Load categories for filters
  loadCategories: async () => {
    try {
      // Mock data - replace with actual Firestore query
      const mockCategories = {
        subjects: [
          { value: 'mathematics', label: 'Mathematics' },
          { value: 'physics', label: 'Physics' },
          { value: 'chemistry', label: 'Chemistry' },
          { value: 'biology', label: 'Biology' }
        ],
        gradeLevels: [
          { value: 'grade_8', label: 'Grade 8' },
          { value: 'grade_9', label: 'Grade 9' },
          { value: 'grade_10', label: 'Grade 10' },
          { value: 'grade_11', label: 'Grade 11' },
          { value: 'grade_12', label: 'Grade 12' }
        ],
        topics: {
          mathematics: [
            { value: 'algebra', label: 'Algebra' },
            { value: 'geometry', label: 'Geometry' },
            { value: 'trigonometry', label: 'Trigonometry' },
            { value: 'calculus', label: 'Calculus' },
            { value: 'statistics', label: 'Statistics' }
          ],
          physics: [
            { value: 'mechanics', label: 'Mechanics' },
            { value: 'waves', label: 'Waves' },
            { value: 'electricity', label: 'Electricity' },
            { value: 'magnetism', label: 'Magnetism' }
          ]
        },
        difficulties: [
          { value: 'easy', label: 'Easy' },
          { value: 'medium', label: 'Medium' },
          { value: 'hard', label: 'Hard' }
        ],
        types: [
          { value: 'solution', label: 'Solution' },
          { value: 'example', label: 'Example' },
          { value: 'theorem', label: 'Theorem' },
          { value: 'formula', label: 'Formula' }
        ]
      };
      
      set({ categories: mockCategories });
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }
}));